import React, { useEffect, useState } from 'react'
import {
  Banner,
  Box,
  Button,
  Card,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  Pagination,
  PaginationList,
  Skeleton,
  Stack,
  StackItem,
  Tag,
  Text,
  VisuallyHidden,
} from '@indeed/ifl-components'
import {
  Table,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHead,
  TableRow,
  TableRowHeader,
} from '@indeed/ifl-components/components/Table'
import { CheckCircle } from '@indeed/ifl-icons'
import { useDispatch, useSelector } from 'react-redux'
import type { DefaultRootState } from 'react-redux'
import { useFormatDateTime } from 'syft-acp-core/lib/hooks'

import { fetchAgencyCascadeSchedulingPlan } from 'syft-acp-core/store/agency-cascade-scheduling-plans/actions'
import type {
  SchedulingPlan as SchedulingPlanData,
  SchedulingPlanLine,
} from 'syft-acp-core/store/agency-cascade-scheduling-plans/types'

const authoredReasonLabels: Record<SchedulingPlanData['authored_reason'], string> = {
  accepted: 'Agency Accepted',
  shift_added: 'Shift added',
  shift_rescheduled: 'Shift rescheduled',
  reallocated: 'Reallocated',
}

const lineStateLabels: Record<SchedulingPlanLine['state'], string> = {
  scheduled: 'Scheduled',
  skipped_late_acceptance: 'Late acceptance',
  skipped_past: 'Shift already passed',
  not_enforceable: 'Not enforceable',
}

const dateTimeFormat = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'short',
} as const

const PLAN_LINES_PER_PAGE = 10

const formatShiftCount = (count: number) => `${count} shift${count === 1 ? '' : 's'}`

type BookingPlanProps = {
  allocationId: number
  deadline: string
  jobId: number
  planId: number
  venueTimezone: string
}

export const BookingPlan = ({ allocationId, deadline, jobId, planId, venueTimezone }: BookingPlanProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [page, setPage] = useState(1)
  const dispatch = useDispatch()
  const plan = useSelector(
    (state: DefaultRootState) =>
      state.agencyCascadeSchedulingPlans.entityDetail[planId] ??
      state.agencyCascadeSchedulingPlans.entityMap[planId],
  )
  const requestState = useSelector(
    (state: DefaultRootState) => state.agencyCascadeSchedulingPlans.requestStateByPlanId[planId],
  )
  const isLoading = !plan && (requestState?.isLoading ?? true)
  const loadError = !plan ? requestState?.error : undefined
  useEffect(() => {
    setPage(1)
  }, [planId])
  const [, formatDateTime] = useFormatDateTime()
  const format = { ...dateTimeFormat, timeZone: venueTimezone }
  const formatDate = (value: string | null) => (value ? formatDateTime(value, { format }) : '—')
  const pageStartIndex = (page - 1) * PLAN_LINES_PER_PAGE

  return (
    <>
      <Box>
        <Text as="strong" level={1} sx={{ display: 'block', textTransform: 'uppercase' }}>
          Booking deadline
        </Text>
        <Text as="div">
          {deadline && !Number.isNaN(Date.parse(deadline)) ? formatDate(deadline) : deadline || '—'}
        </Text>
        <Button
          type="button"
          variant="text"
          size="sm"
          aria-label={`View plan for agency allocation ${allocationId}`}
          sx={{ fontSize: 2, fontWeight: 'normal' }}
          onClick={event => {
            event.preventDefault()
            event.stopPropagation()
            if (!plan && !requestState?.isLoading) dispatch(fetchAgencyCascadeSchedulingPlan({ planId }))
            setIsOpen(true)
          }}
        >
          View plan
        </Button>
      </Box>

      {isOpen && (
        <Dialog size="xl" open portaled onOpenChange={setIsOpen}>
          <DialogContent onClick={event => event.stopPropagation()}>
            <DialogHeader description={`Job #${jobId} · agency allocation #${allocationId}`}>
              Scheduling plan
            </DialogHeader>
            <DialogBody sx={{ textAlign: 'left' }} aria-busy={isLoading}>
              {loadError && (
                <Banner status="critical" sx={{ marginBlockEnd: 4 }}>
                  {loadError}
                </Banner>
              )}
              {isLoading && (
                <Box role="status">
                  <VisuallyHidden>Loading scheduling plan…</VisuallyHidden>
                </Box>
              )}
              {(plan || isLoading) && (
                <Stack
                  role="region"
                  aria-label="Scheduling plan details"
                  aria-hidden={isLoading || undefined}
                  alignment="fill"
                  spacing={2}
                  wrap
                  sx={{
                    marginBlockEnd: 5,
                    '& h3': { margin: 0 },
                    '& p': { marginBlockStart: 1, marginBlockEnd: 0 },
                  }}
                >
                  {plan
                    ? [
                        { title: 'Created On', value: formatDate(plan.authored_at) },
                        {
                          title: 'Plan version reason',
                          value: authoredReasonLabels[plan.authored_reason],
                        },
                        {
                          title: 'Next evaluation',
                          value: plan.next_evaluation_at
                            ? formatDate(plan.next_evaluation_at)
                            : 'None scheduled',
                        },
                        { title: 'Active plan', value: `v${plan.version}` },
                      ].map(({ title, value }) => (
                        <StackItem key={title} sx={{ flex: '1 1 12rem' }}>
                          <Card>
                            <Text as="h3" level={2}>
                              {title}
                            </Text>
                            <Text as="p" level={3}>
                              {value}
                            </Text>
                          </Card>
                        </StackItem>
                      ))
                    : Array.from({ length: 4 }, (_, index) => (
                        <StackItem key={index} sx={{ flex: '1 1 12rem' }}>
                          <Card>
                            <Skeleton variant="heading" size="sm" sx={{ marginBlockEnd: 2 }} />
                            <Skeleton />
                          </Card>
                        </StackItem>
                      ))}
                </Stack>
              )}

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  marginBlockEnd: 2,
                }}
              >
                <Text
                  as="h3"
                  level={2}
                  variant="secondary"
                  sx={{ margin: 0, textTransform: 'uppercase', letterSpacing: '0.05em' }}
                >
                  Shifts and deadlines
                </Text>
                {plan ? (
                  <Text level={2} variant="secondary">
                    {formatShiftCount(plan.lines.length)}
                  </Text>
                ) : (
                  isLoading && (
                    <Box aria-hidden="true" sx={{ inlineSize: '5rem' }}>
                      <Skeleton />
                    </Box>
                  )
                )}
              </Box>
              <Box sx={{ overflowX: 'auto' }}>
                <Table aria-label="Shifts and deadlines" aria-busy={isLoading}>
                  <TableHead>
                    <TableRow>
                      <TableColumnHeader>Shift</TableColumnHeader>
                      <TableColumnHeader>Start</TableColumnHeader>
                      <TableColumnHeader>Booking deadline</TableColumnHeader>
                      <TableColumnHeader>Filled / required</TableColumnHeader>
                      <TableColumnHeader>Status</TableColumnHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {plan?.lines.slice(pageStartIndex, pageStartIndex + PLAN_LINES_PER_PAGE).map(planLine => (
                      <TableRow key={planLine.agency_shift_id}>
                        <TableRowHeader>#{planLine.shift_id}</TableRowHeader>
                        <TableCell>{formatDate(planLine.start_time)}</TableCell>
                        <TableCell>
                          <Text as="strong">{formatDate(planLine.deadline_at)}</Text>
                        </TableCell>
                        <TableCell>
                          {planLine.allocation_without_buffer > 0 &&
                          planLine.workers_provided >= planLine.allocation_without_buffer ? (
                            <Tag status="success">
                              <CheckCircle size="sm" aria-hidden="true" />
                              <Box as="span">
                                <VisuallyHidden>Fully filled: </VisuallyHidden>
                                {`${planLine.workers_provided} / ${planLine.allocation_without_buffer}`}
                              </Box>
                            </Tag>
                          ) : (
                            <Text as="strong">{`${planLine.workers_provided} / ${planLine.allocation_without_buffer}`}</Text>
                          )}
                        </TableCell>
                        <TableCell>
                          <Tag
                            status={planLine.state === 'scheduled' ? 'info' : 'neutral'}
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            {lineStateLabels[planLine.state]}
                          </Tag>
                        </TableCell>
                      </TableRow>
                    ))}
                    {isLoading &&
                      Array.from({ length: 5 }, (_, index) => (
                        <TableRow key={index}>
                          <TableRowHeader>
                            <Skeleton />
                          </TableRowHeader>
                          <TableCell>
                            <Skeleton />
                          </TableCell>
                          <TableCell>
                            <Skeleton />
                          </TableCell>
                          <TableCell>
                            <Skeleton />
                          </TableCell>
                          <TableCell>
                            <Skeleton />
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </Box>
              {plan && plan.lines.length > PLAN_LINES_PER_PAGE && (
                <Pagination
                  totalItems={plan.lines.length}
                  itemsPerPage={PLAN_LINES_PER_PAGE}
                  page={page}
                  alignment="center"
                  nextAriaLabel="Next page of shifts"
                  prevAriaLabel="Previous page of shifts"
                  onPageChange={setPage}
                  sx={{ marginBlockStart: 4 }}
                >
                  <PaginationList />
                </Pagination>
              )}
            </DialogBody>
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}
